/**
 * 日本の電話番号フォーマットユーティリティ
 * 入力された数字に対して、先頭桁のパターンに応じてハイフンを自動挿入する。
 *
 * 対応パターン:
 *   携帯電話 (070/080/090): 3-4-4  例: 090-1234-5678
 *   IP電話 (050):           3-4-4  例: 050-1234-5678
 *   フリーダイヤル (0120):  4-3-3  例: 0120-123-456
 *   フリーコール (0800):    4-3-4  例: 0800-123-4567
 *   東京/大阪等 (03/06):    2-4-4  例: 03-1234-5678
 *   その他固定電話:         3-4-4  例: 045-123-4567
 */

/** ハイフンを除去して数字のみ返す */
export const stripPhoneHyphens = (value: string): string => {
  return value.replace(/[^0-9]/g, '');
};

/** 先頭桁からハイフン挿入位置（各セグメントの桁数）を判定する */
const getPhoneSegments = (digits: string): [number, number, number] => {
  // 携帯電話・IP電話: 070, 080, 090, 050 → 3-4-4
  if (/^0[5789]0/.test(digits)) {
    return [3, 4, 4];
  }
  // フリーダイヤル: 0120 → 4-3-3
  if (/^0120/.test(digits)) {
    return [4, 3, 3];
  }
  // フリーコール: 0800 → 4-3-4
  if (/^0800/.test(digits)) {
    return [4, 3, 4];
  }
  // 東京(03), 大阪(06) 等の2桁市外局番 → 2-4-4
  if (/^0[36]/.test(digits) && !/^0[36]0/.test(digits)) {
    return [2, 4, 4];
  }
  // その他固定電話（0XX-） → 3-4-4
  return [3, 4, 4];
};

/**
 * 電話番号をフォーマットする。
 * 数字以外の文字は除去し、先頭桁パターンに応じてハイフンを自動挿入する。
 * 入力途中（桁数が足りない状態）でも適切にフォーマットする。
 *
 * @param value - ユーザーが入力した電話番号文字列（ハイフン含む可能性あり）
 * @returns ハイフン付きでフォーマットされた電話番号
 */
export const formatPhoneNumber = (value: string): string => {
  const digits = stripPhoneHyphens(value);

  if (digits.length === 0) return '';

  const [seg1, seg2, seg3] = getPhoneSegments(digits);
  const maxLength = seg1 + seg2 + seg3;

  // 最大桁数で切り詰め
  const trimmed = digits.slice(0, maxLength);

  // セグメントに分割してハイフンで結合
  if (trimmed.length <= seg1) {
    return trimmed;
  }
  if (trimmed.length <= seg1 + seg2) {
    return `${trimmed.slice(0, seg1)}-${trimmed.slice(seg1)}`;
  }
  return `${trimmed.slice(0, seg1)}-${trimmed.slice(seg1, seg1 + seg2)}-${trimmed.slice(seg1 + seg2)}`;
};
